import { useEffect, useRef, useState } from 'react'
import { Portal } from '../Portal'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ArrowUpRight, Check, Copy, X } from 'lucide-react'
import { Sheet } from '../Sheet'

const DOCS = 'https://langwatch.ai/docs/self-hosting/overview'
const SIGNUP = 'https://app.langwatch.ai/'
const SIGNIN = 'https://app.langwatch.ai/'
const RUN_CMD = 'npx @langwatch/server'

/**
 * SelfHostMenu — the "Self-host in 15 min" CTA with a hover mega-popover.
 *
 * On hover a full-width dialog drops under the button: its outline is *drawn*
 * around the rectangle first (an SVG stroke animation), then the three ways to
 * run LangWatch reveal in sequence — Cloud, your machine (one command), and the
 * Helm chart. Clicking the button still navigates to the self-hosting docs, so
 * it degrades gracefully where hover isn't available.
 */
export function SelfHostMenu({ inverse = false }: { inverse?: boolean }) {
  const [open, setOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [box, setBox] = useState<{ top: number; cx: number } | null>(null)
  const triggerRef = useRef<HTMLAnchorElement>(null)
  const closeTimer = useRef<number | undefined>(undefined)

  const measure = () => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setBox({ top: r.bottom, cx: r.left + r.width / 2 })
  }
  const openNow = () => {
    window.clearTimeout(closeTimer.current)
    measure()
    setOpen(true)
  }
  const closeSoon = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 130)
  }

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])
  useEffect(() => {
    if (!open) return
    const onMove = () => measure()
    window.addEventListener('scroll', onMove, { passive: true })
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  // Hover popover is desktop-only. On mobile (either narrow viewport or no
  // hover capability), intercept the trigger click and open a full-screen Sheet
  // instead of navigating straight to the docs.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const narrow = window.innerWidth < 768
    const noHover = window.matchMedia('(hover: none)').matches
    if (narrow || noHover) {
      e.preventDefault()
      setSheetOpen(true)
    }
  }

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <a
        ref={triggerRef}
        href={DOCS}
        onClick={handleClick}
        className={
          inverse
            ? 'inline-flex items-center rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white'
            : 'btn-secondary !px-5 !py-3'
        }
      >
        Self-host in 15 min
      </a>

      <Portal>
        <AnimatePresence>
          {open && box && (
            <Panel
              top={box.top}
              cx={box.cx}
              onEnter={openNow}
              onLeave={closeSoon}
            />
          )}
        </AnimatePresence>
      </Portal>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        ariaLabel="Self-host LangWatch"
        size="md"
      >
        <SheetContent onClose={() => setSheetOpen(false)} />
      </Sheet>
    </span>
  )
}

function SheetContent({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col gap-7 p-6 pb-10 sm:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[11.5px] font-medium uppercase tracking-[0.22em] text-ink-400">
            Self-host
          </div>
          <h2 className="display mt-2 text-[28px] leading-[1.05] text-ink-950">
            Three ways to run LangWatch.
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-500 transition hover:bg-ink-100 hover:text-ink-900"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-col gap-7 border-t border-ink-100 pt-7">
        <div className="flex flex-col">
          <ColHead
            title="Start in the cloud"
            desc="We run the full platform. Be sending traces in under a minute."
          />
          <div className="flex flex-wrap gap-2.5">
            <a href={SIGNIN} className="btn-secondary !px-5 !py-2.5 !text-[13.5px]">
              Sign in
            </a>
            <a href={SIGNUP} className="btn-primary !px-5 !py-2.5 !text-[13.5px]">
              Sign up
            </a>
          </div>
          <div className="mt-5">
            <ComplianceBadge />
          </div>
        </div>

        <div className="flex flex-col border-t border-ink-100 pt-7">
          <ColHead
            title="Run on your machine"
            desc="The whole stack on your laptop, ready in minutes."
          />
          <Terminal />
        </div>

        <div className="flex flex-col border-t border-ink-100 pt-7">
          <ColHead
            title="Self-Host with Helm charts"
            desc="Ship to your own cluster, production-ready."
          />
          <HelmBlock />
          <a
            href={DOCS}
            className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-[#1e3aa6] transition-colors hover:text-[#152a78]"
          >
            Read the self-hosting docs
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  )
}

const GUTTER = 24
const MAX_W = 1440

function Panel({
  top,
  cx,
  onEnter,
  onLeave,
}: {
  top: number
  cx: number
  onEnter: () => void
  onLeave: () => void
}) {
  const reduce = useReducedMotion()

  // Centre the panel and cap it at 1440px, matching the site container.
  const vw = typeof window !== 'undefined' ? window.innerWidth : MAX_W
  const width = Math.min(vw - GUTTER * 2, MAX_W)
  const panelLeft = Math.max(GUTTER, (vw - width) / 2)
  // The caret points up at the trigger's centre, relative to the panel's left
  // edge and kept clear of the rounded corner.
  const caretLeft = Math.min(Math.max(cx - panelLeft, 30), width - 30)

  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.05, delayChildren: reduce ? 0 : 0.04 },
    },
  }
  const item = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.36, ease: [0.22, 1, 0.36, 1] as const },
    },
  }

  return (
    <motion.div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        position: 'fixed',
        top: top + 12,
        left: panelLeft,
        width,
        zIndex: 60,
      }}
      initial={reduce ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6, transition: { duration: 0.15 } }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <div className="relative">
        <Caret left={caretLeft} reduce={!!reduce} />
        <div className="overflow-hidden rounded-2xl border border-black/[0.09] bg-white shadow-[0_40px_100px_-30px_rgba(20,15,40,0.42),0_12px_40px_-14px_rgba(20,15,40,0.16)]">
        <motion.div
          variants={container}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="relative"
        >
          <div className="grid grid-cols-1 md:grid-cols-3">
            {/* Cloud */}
            <motion.div variants={item} className="flex flex-col p-7 md:p-9">
              <ColHead
                title="Start in the cloud"
                desc="We run the full platform. Be sending traces in under a minute."
              />
              <div className="flex flex-wrap gap-2.5">
                <a
                  href={SIGNIN}
                  className="btn-secondary !px-5 !py-2.5 !text-[13.5px]"
                >
                  Sign in
                </a>
                <a
                  href={SIGNUP}
                  className="btn-primary !px-5 !py-2.5 !text-[13.5px]"
                >
                  Sign up
                </a>
              </div>
              <div className="mt-5">
                <ComplianceBadge />
              </div>
            </motion.div>

            {/* Your machine */}
            <motion.div
              variants={item}
              className="flex flex-col p-7 md:border-l md:border-ink-100 md:p-9"
            >
              <ColHead
                title="Run on your machine"
                desc="The whole stack on your laptop, ready in minutes."
              />
              <Terminal />
            </motion.div>

            {/* Helm */}
            <motion.div
              variants={item}
              className="flex flex-col p-7 md:border-l md:border-ink-100 md:p-9"
            >
              <ColHead
                title="Self-Host with Helm charts"
                desc="Ship to your own cluster, production-ready."
              />
              <HelmBlock />
              <a
                href={DOCS}
                className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-[#1e3aa6] transition-colors hover:text-[#152a78]"
              >
                Read the self-hosting docs
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>
          </div>
        </motion.div>
        </div>
      </div>
    </motion.div>
  )
}

/** The little arrow pointing up from the panel toward the trigger button. */
function Caret({ left, reduce }: { left: number; reduce: boolean }) {
  return (
    <motion.div
      aria-hidden
      className="absolute z-20 h-3.5 w-3.5 border-l border-t bg-white"
      style={{
        left,
        top: -7,
        transform: 'translateX(-50%) rotate(45deg)',
        borderColor: 'rgba(0,0,0,0.09)',
        borderTopLeftRadius: 3,
        boxShadow: '-3px -3px 6px -3px rgba(20,15,40,0.06)',
      }}
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, delay: 0.08 }}
    />
  )
}

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button type="button" onClick={onCopy} aria-label="Copy" className={className}>
      {copied ? (
        <Check className="h-4 w-4 text-moss-accent" strokeWidth={2.6} />
      ) : (
        <Copy className="h-4 w-4" strokeWidth={2} />
      )}
    </button>
  )
}

function ColHead({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="mb-5">
      <h3 className="text-[19px] font-semibold tracking-[-0.01em] text-ink-900">
        {title}
      </h3>
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-500">{desc}</p>
    </div>
  )
}

function ComplianceBadge() {
  const items: [string, string][] = [
    ['ISO', '27001'],
    ['GDPR', 'Compliant'],
    ['EU', 'Hosted'],
  ]
  return (
    <ul className="space-y-1.5 text-[12.5px]">
      {items.map(([a, b]) => (
        <li key={a} className="flex items-center gap-2">
          <Check className="h-3.5 w-3.5 text-moss-accent" strokeWidth={2.5} />
          <span>
            <span className="font-semibold text-ink-700">{a}</span>{' '}
            <span className="text-ink-400">{b}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

function Terminal() {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0f0e13] px-4 py-3 font-mono text-[12.5px]">
      <span className="min-w-0 flex-1 truncate">
        <span className="text-[#8fb0f2]">$</span>{' '}
        <span className="text-[#e8ecf8]">npx @langwatch/server</span>
      </span>
      <CopyButton
        text={RUN_CMD}
        className="shrink-0 text-white/40 transition-colors hover:text-white/85"
      />
    </div>
  )
}

const HELM = `helm repo add langwatch https://langwatch.github.io/langwatch
helm repo update
helm pull langwatch/langwatch --untar
helm install langwatch ./langwatch \\
  --namespace langwatch --create-namespace \\
  -f langwatch/examples/values-local.yaml`

function HelmBlock() {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-xl border border-ink-100 bg-ink-50/70 p-4 pr-11 font-mono text-[11px] leading-relaxed text-ink-600">
        {HELM}
      </pre>
      <CopyButton
        text={HELM}
        className="absolute right-2.5 top-2.5 rounded-md border border-ink-100 bg-white/85 p-1.5 text-ink-400 transition-colors hover:text-ink-700"
      />
    </div>
  )
}
